import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './app/auth';
import { ProtectedRoute } from './app/auth';
import { PublicLayout, AppLayout } from './app/layouts';
import { Download, X } from 'lucide-react';
import {
  setupPwaInstallPrompt,
  triggerPwaInstall,
  dismissPwaInstallPrompt,
  isStandalonePwa,
} from './utils/pwaInstall';
import { hasApiBaseUrlConfigurada, probarConexionServidor } from './services/apiConfig';
import { isNativePlatform } from './utils/platform';

// Auth
import { LoginPage, ServerConfigPage } from './modules/auth/pages';

// Test
import { TestPage } from './modules/test/pages';

// POS
import { POSPage } from './modules/pos/pages';

// Local
import {
  LocalDashboardPage,
  LocalesPage,
  LocalReportesPage,
  LocalHistoricoPage,
  LocalListasPage,
  LocalSaldoPage,
  LocalRestringidosPage,
  LocalConfigTicketPage,
  LocalGanadoresPage,
  PrinterConfigPage,
} from './modules/local/pages';
import { VendedorPremiosPage } from './modules/local/pages/VendedorPremiosPage';

// Banca
import {
  BancaDashboardPage,
  SorteosPage,
  RestringidosPage,
  NumerosGanadoresPage,
  PremiosPage,
  ListasPage,
  BancaReportesPage,
  SaldosPage,
  BancaVendedoresPage,
  BancaLocalesSorteosPage,
  AuditoriaPage,
  PaquetesReglasPage,
  SupervisorAbonosPage,
} from './modules/banca/pages';
import { AnaliticaDashboard } from './modules/analitica/pages/AnaliticaDashboard';
import { ConsultasPage } from './modules/consultas/pages/ConsultasPage';
import { BanqueroResumenPage } from './modules/banquero/pages/BanqueroResumenPage';
import { BanqueroSaldosPage } from './modules/banquero/pages/BanqueroSaldosPage';
import { BanqueroListasPage } from './modules/banquero/pages/BanqueroListasPage';

// Admin
import {
  AdminDashboardPage,
  BancasPage,
  UsuariosPage,
  SuscripcionesPage,
  PagosPage,
  SeguridadPage
} from './modules/admin/pages';

// Redirección a la ruta base según el rol del usuario autenticado
const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'LOCAL' && user.esPropio) return <Navigate to="/local/premios" replace />;
  const redirects: Record<string, string> = {
    ADMIN: '/admin',
    BANCA: '/banca',
    LOCAL: '/local',
    SUPERVISOR: '/supervisor',
    BANQUERO: '/banquero',
    VENDEDOR_CON_CAJA: '/local/pos',
    VENDEDOR_SIN_CAJA: '/local/pos',
    VENDEDOR: '/local/pos',
  };
  return <Navigate to={redirects[user.role] ?? '/login'} replace />;
};

const LoginBootstrapPage: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [mensajeConfig, setMensajeConfig] = useState('');

  useEffect(() => {
    let activo = true;

    // Web: ir directo al login, sin verificar servidor
    if (!isNativePlatform()) {
      setCargando(false);
      return;
    }

    // APK/Capacitor: verificar servidor configurado
    const init = async () => {
      if (!hasApiBaseUrlConfigurada()) {
        if (!activo) return;
        setMensajeConfig('Debe configurar un servidor para continuar.');
        setMostrarConfig(true);
        setCargando(false);
        return;
      }

      const conexion = await probarConexionServidor();
      if (!activo) return;

      if (!conexion.ok) {
        setMensajeConfig('No se pudo conectar con el servidor seleccionado');
        setMostrarConfig(true);
        setCargando(false);
        return;
      }

      setMostrarConfig(false);
      setMensajeConfig('');
      setCargando(false);
    };

    init();
    return () => {
      activo = false;
    };
  }, []);

  if (cargando) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">Verificando servidor...</p>
      </div>
    );
  }

  if (mostrarConfig) {
    return <ServerConfigPage bloqueoInicio mensajeInicial={mensajeConfig} />;
  }

  return <LoginPage />;
};

const App: React.FC = () => {
  const [mostrarInstalacionPwa, setMostrarInstalacionPwa] = useState(false);
  const visibleModalPwaRef = useRef(false);

  useEffect(() => {
    if (isStandalonePwa()) {
      return;
    }

    const unsubscribe = setupPwaInstallPrompt((available) => {
      if (visibleModalPwaRef.current === available) {
        return;
      }
      visibleModalPwaRef.current = available;
      setMostrarInstalacionPwa(available);
    });

    return unsubscribe;
  }, []);

  const instalarPwa = async () => {
    const accepted = await triggerPwaInstall();
    if (accepted) {
      visibleModalPwaRef.current = false;
      setMostrarInstalacionPwa(false);
    }
  };

  const ocultarInstalacionPwa = () => {
    dismissPwaInstallPrompt();
    visibleModalPwaRef.current = false;
    setMostrarInstalacionPwa(false);
  };

  return (
    <AuthProvider>
      {mostrarInstalacionPwa && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-sm bg-blue-600 text-white shadow-xl rounded-xl p-3 border border-blue-500">
          <div className="flex items-start gap-2">
            <Download size={18} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Instalar GRUPO SONI</p>
              <p className="text-xs text-blue-100">Agrega la app al inicio para usarla como POS en Android.</p>
            </div>
            <button
              onClick={ocultarInstalacionPwa}
              className="text-blue-100 hover:text-white"
              aria-label="Ocultar instalación"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={instalarPwa}
              className="flex-1 bg-white text-blue-700 text-sm font-semibold py-1.5 rounded-lg hover:bg-blue-50"
            >
              Instalar
            </button>
            <button
              onClick={ocultarInstalacionPwa}
              className="px-3 py-1.5 text-sm rounded-lg border border-blue-400 text-blue-100 hover:text-white"
            >
              Luego
            </button>
          </div>
        </div>
      )}
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginBootstrapPage />} />
            <Route path="/servidor" element={isNativePlatform() ? <ServerConfigPage /> : <Navigate to="/login" replace />} />
            <Route path="/test" element={<TestPage />} />
          </Route>

          {/* Protected Routes */}
          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            {/* Redirect root a la ruta base del rol autenticado */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* Compatibilidad legado: cualquier ruta /vendedor redirige al POS local */}
            <Route path="/vendedor/*" element={<Navigate to="/local/pos" replace />} />

            {/* Legacy Routes */}
            <Route path="/pos" element={<Navigate to="/local/pos" replace />} />
            <Route path="/caja" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/mis-ventas" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/clientes" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/clientes/:id" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/clientes/cartera" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/clientes/rapidos" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/caja-loteria" element={<Navigate to="/local" replace />} />
            <Route path="/loteria-caja" element={<Navigate to="/local" replace />} />
            <Route path="/local/cierre-loterias" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/local/recargas" element={<Navigate to="/local/reportes" replace />} />

            {/* LOCAL Routes */}
            <Route path="/local/dashboard" element={<Navigate to="/local" replace />} />
            <Route path="/local" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/local/pos" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA', 'VENDEDOR']}>
                <POSPage />
              </ProtectedRoute>
            } />
            <Route path="/local/locales" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalesPage />
              </ProtectedRoute>
            } />
            <Route path="/local/vendedores" element={<Navigate to="/local" replace />} />
            <Route path="/local/reglas" element={<Navigate to="/local/listas" replace />} />
            <Route path="/local/listas" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalListasPage />
              </ProtectedRoute>
            } />
            <Route path="/local/reportes" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/local/historico" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA', 'VENDEDOR']}>
                <LocalHistoricoPage />
              </ProtectedRoute>
            } />
            <Route path="/local/saldos" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalSaldoPage />
              </ProtectedRoute>
            } />
            <Route path="/local/ticket-config" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalConfigTicketPage />
              </ProtectedRoute>
            } />
            <Route path="/local/printer-config" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA', 'VENDEDOR']}>
                <PrinterConfigPage />
              </ProtectedRoute>
            } />
            <Route path="/local/validacion-sinpe" element={<Navigate to="/local/reportes" replace />} />
            <Route path="/local/ganadores" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <LocalGanadoresPage />
              </ProtectedRoute>
            } />
            <Route path="/local/premios" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR', 'VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <VendedorPremiosPage />
              </ProtectedRoute>
            } />

            <Route path="/local/caja-loteria" element={<Navigate to="/local/reportes" replace />} />


            {/* BANCA Routes */}
            <Route path="/banca/dashboard" element={<Navigate to="/banca" replace />} />
            <Route path="/banca" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/sorteos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <SorteosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/listas" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <ListasPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/vendedores" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaVendedoresPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/premios" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <PremiosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/restringidos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <RestringidosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/ganadores" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <NumerosGanadoresPage />
              </ProtectedRoute>
            } />
            <Route path="/banquero/ganadores" element={
              <ProtectedRoute allowedRoles={['BANQUERO']}>
                <NumerosGanadoresPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/reportes" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/saldos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <SaldosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/locales-sorteos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaLocalesSorteosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/analitica" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <AnaliticaDashboard />
              </ProtectedRoute>
            } />
            <Route path="/banca/auditoria" element={
              <ProtectedRoute allowedRoles={['BANCA', 'ADMIN']}>
                <AuditoriaPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/paquetes-reglas" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <PaquetesReglasPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/consultas" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <ConsultasPage />
              </ProtectedRoute>
            } />

            {/* SUPERVISOR Routes — reuse BANCA components in read-only mode */}
            <Route path="/supervisor" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <Navigate to="/supervisor/listas" replace />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/listas" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <ListasPage />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/reportes" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <BancaReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/consultas" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <ConsultasPage />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/saldos" element={
              <Navigate to="/supervisor/abonos" replace />
            } />
            <Route path="/supervisor/abonos" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <SupervisorAbonosPage />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/premios" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <PremiosPage />
              </ProtectedRoute>
            } />
            <Route path="/supervisor/ganadores" element={
              <ProtectedRoute allowedRoles={['SUPERVISOR']}>
                <NumerosGanadoresPage />
              </ProtectedRoute>
            } />

            {/* BANQUERO Routes */}
            <Route path="/banquero" element={
              <ProtectedRoute allowedRoles={['BANQUERO']}>
                <BanqueroResumenPage />
              </ProtectedRoute>
            } />
            <Route path="/banquero/listas" element={
              <ProtectedRoute allowedRoles={['BANQUERO']}>
                <BanqueroListasPage />
              </ProtectedRoute>
            } />
            <Route path="/banquero/reportes" element={
              <ProtectedRoute allowedRoles={['BANQUERO']}>
                <BancaReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/banquero/saldos" element={
              <ProtectedRoute allowedRoles={['BANQUERO']}>
                <BanqueroSaldosPage />
              </ProtectedRoute>
            } />

            {/* ADMIN Routes */}
            <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/bancas" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <BancasPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/usuarios" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <UsuariosPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/suscripciones" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <SuscripcionesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/pagos" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <PagosPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/seguridad" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <SeguridadPage />
              </ProtectedRoute>
            } />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
